/**
 * pendingAccessIntent - 登录后恢复意图（spec §7.5）
 *
 * 场景：未登录用户点击"提交 Idea"按钮，L1 预判判定 deny login_required，
 * presentAccessDecision 打开 loginModal，同时 recordPendingAccessIntent
 * 记录该意图。用户登录成功后，replayPendingAccessIntent 检查意图是否仍
 * 有效（页面未变、资源仍存在），若有效则提示用户"是否继续之前的操作"，
 * 用户确认后重新执行 action。
 *
 * 规则（spec §7.5）：
 * - 最长保留 10 分钟（10 * 60 * 1000 ms）
 * - 只执行一次
 * - 登录后重新鉴权（不能凭旧决策直接执行，必须再过一次 accessResolver）
 * - 页面和资源仍有效才执行
 * - 不保存文件二进制（如 AI Create 上传的图片）
 * - 表单只保存必要字段（不保存敏感字段如密码）
 * - 恢复失败时给出正常状态提示，不循环弹登录
 *
 * 安全边界：
 * - 不存储任何敏感字段（密码、token、完整文件内容）
 * - 不存储完整 action 字符串中嵌入的参数（如 submitIdea("hello") 中的 "hello"），
 *   只存储 action 名称，让用户重新填写表单内容
 * - 不使用 localStorage；只使用当前 tab 的 sessionStorage，并绑定随机 session id
 * - 已登录时记录 userId，恢复时必须仍为同一用户
 */

import type { AccessAction } from "../../domain/types/accessTypes";
import { normalizePrototypeAction } from './normalizePrototypeAction';
import { resolveClientAccess, type ClientAccessContext, type ClientAccessDecision } from './accessResolver';

const PENDING_INTENT_TTL_MS = 10 * 60 * 1000; // 10 分钟
const PENDING_INTENT_KEY = "__deepmoxPendingAccessIntent";
const PENDING_INTENT_SESSION_KEY = '__deepmoxPendingAccessSession';

type PendingIntentWindow = Window & {
	[PENDING_INTENT_KEY]?: PendingAccessIntent;
	[PENDING_INTENT_SESSION_KEY]?: string;
};

export interface PendingAccessIntent {
	/** AccessAction key */
	action: AccessAction;
	/** 触发 action 的原始字符串（用于登录后自动重新触发） */
	rawAction: string;
	/** 记录时若已登录，恢复必须仍为同一用户。 */
	recordedUserId?: string | null;
	/** 当前浏览器 tab 会话的随机绑定值。 */
	browserSessionId: string;
	/** 记录时间（Date.now()） */
	recordedAt: number;
}

export interface RecordPendingAccessIntentInput {
	action: AccessAction;
	rawAction: string;
	recordedUserId?: string | null;
}

/**
 * 记录 pendingAccessIntent（spec §7.5）。
 *
 * 一次只保留一个意图：新记录覆盖旧的。
 * 如果用户在登录前点了多个受限按钮，我们只恢复最后一个（UX 合理：
 * 用户最后的意图最接近当前需求）。
 */
export function recordPendingAccessIntent(intent: RecordPendingAccessIntentInput): void {
	if (typeof window === "undefined") return;
	if (!isReplayableAction(intent.action)) return;
	if (typeof intent.rawAction !== 'string' || intent.rawAction.length === 0 || intent.rawAction.length > 512) return;

	const browserSessionId = getOrCreateBrowserSessionId();
	if (!browserSessionId) return;
	const full: PendingAccessIntent = {
		action: intent.action,
		rawAction: intent.rawAction,
		...(isSafeUserId(intent.recordedUserId) ? { recordedUserId: intent.recordedUserId } : {}),
		browserSessionId,
		recordedAt: Date.now(),
	};
	const storage = getSessionStorage();
	if (storage) {
		try {
			storage.setItem(PENDING_INTENT_KEY, JSON.stringify(full));
		} catch {
			// Safari private mode and hardened browsers can reject storage writes.
		}
	}
	(window as PendingIntentWindow)[PENDING_INTENT_KEY] = full;
}

/**
 * 读取 pendingAccessIntent。返回 null 表示无意图或已过期。
 *
 * 过期判断：recordedAt + TTL < now -> 已过期，清空并返回 null。
 */
export function readPendingAccessIntent(): PendingAccessIntent | null {
	if (typeof window === "undefined") return null;
	const w = window as PendingIntentWindow;
	const intent = readStoredIntent() ?? w[PENDING_INTENT_KEY];
	if (!intent) return null;
	const browserSessionId = getOrCreateBrowserSessionId();
	if (!isValidPendingIntent(intent) || !browserSessionId || intent.browserSessionId !== browserSessionId) {
		clearPendingAccessIntent();
		return null;
	}
	if (Date.now() - intent.recordedAt > PENDING_INTENT_TTL_MS) {
		// 过期，清空
		delete w[PENDING_INTENT_KEY];
		return null;
	}
	return intent;
}

/**
 * 清空 pendingAccessIntent。
 *
 * 在以下情况调用：
 * - 登录后 replay 完成（无论成功或失败）
 * - 用户主动关闭登录模态框
 * - 页面视图切换（showView），放弃旧意图
 */
export function clearPendingAccessIntent(): void {
	if (typeof window === "undefined") return;
	delete (window as PendingIntentWindow)[PENDING_INTENT_KEY];
	try {
		getSessionStorage()?.removeItem(PENDING_INTENT_KEY);
	} catch {
		// Best-effort cleanup; the expired/session-bound payload remains unusable.
	}
}

/**
 * 判断某个 AccessAction 是否适合作为 pendingAccessIntent 恢复。
 *
 * 有些 action 恢复后无意义（如 chapter.read - 登录后用户已经在别的页面），
 * 或恢复后必然失败（如 upload.* - 文件已经被释放）。
 * 这些 action 不应记录 intent，直接打开登录即可。
 */
export function isReplayableAction(action: AccessAction | null): boolean {
	if (!action) return false;
	// 只记录"写操作"类 intent，读操作恢复意义不大（用户登录后可以自己重新点击）
	const replayable: ReadonlySet<AccessAction> = new Set<AccessAction>([
		"community.idea.submit",
		"community.idea.vote",
		"community.comment.submit",
		"activity.favorite.write",
		"activity.topicVote.write",
		"ai.course.create",
		"billing.manage",
		"account.manage",
	]);
	return replayable.has(action);
}

/**
 * Atomically consume and replay the most recent intent after login.
 *
 * Clearing happens before authorization/execution so an exception cannot cause
 * a replay loop. The raw action is parsed again and must still map to the same
 * policy action; the current session/plan is then evaluated from scratch.
 */
export async function replayPendingAccessIntent(
	context: ClientAccessContext,
	execute: (rawAction: string) => Promise<void>,
	onDenied?: (decision: ClientAccessDecision, intent: PendingAccessIntent) => void | Promise<void>,
): Promise<'none' | 'denied' | 'replayed'> {
	const intent = readPendingAccessIntent();
	if (!intent) return 'none';
	clearPendingAccessIntent();
	if (!isReplayableAction(intent.action)) return 'denied';
	if (intent.recordedUserId && intent.recordedUserId !== context.userId) return 'denied';
	const normalized = normalizePrototypeAction(intent.rawAction);
	if (normalized.action !== intent.action) return 'denied';
	const decision = resolveClientAccess(intent.action, {
		...context,
		resource: normalized.resource ?? context.resource,
	});
	if (decision.kind !== 'allow') {
		await onDenied?.(decision, intent);
		return 'denied';
	}

	await execute(intent.rawAction);
	return 'replayed';
}

/**
 * 计算从意图记录到现在的毫秒数（测试用）。
 */
export function intentAgeMs(intent: PendingAccessIntent): number {
	return Date.now() - intent.recordedAt;
}

/** 测试专用：重置所有 pending intent（不暴露给生产代码） */
export function __resetForTests(): void {
	if (typeof window === "undefined") return;
	clearPendingAccessIntent();
	const w = window as PendingIntentWindow;
	delete w[PENDING_INTENT_SESSION_KEY];
	try {
		getSessionStorage()?.removeItem(PENDING_INTENT_SESSION_KEY);
	} catch {
		// Test cleanup is best effort.
	}
}

function isSafeUserId(value: string | null | undefined): value is string {
	return typeof value === 'string' && value.length > 0 && value.length <= 160 && /^[A-Za-z0-9._:@-]+$/.test(value);
}

function getSessionStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
	try {
		return window.sessionStorage ?? null;
	} catch {
		return null;
	}
}

function createBrowserSessionId(): string {
	if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
	const bytes = new Uint8Array(16);
	if (typeof globalThis.crypto?.getRandomValues === 'function') {
		globalThis.crypto.getRandomValues(bytes);
		return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function getOrCreateBrowserSessionId(): string | null {
	if (typeof window === 'undefined') return null;
	const w = window as PendingIntentWindow;
	const storage = getSessionStorage();
	try {
		const stored = storage?.getItem(PENDING_INTENT_SESSION_KEY);
		if (stored && stored.length <= 160) {
			w[PENDING_INTENT_SESSION_KEY] = stored;
			return stored;
		}
	} catch {
		// Fall through to the in-memory tab binding.
	}
	if (w[PENDING_INTENT_SESSION_KEY]) return w[PENDING_INTENT_SESSION_KEY] ?? null;
	const created = createBrowserSessionId();
	w[PENDING_INTENT_SESSION_KEY] = created;
	try {
		storage?.setItem(PENDING_INTENT_SESSION_KEY, created);
	} catch {
		// The in-memory binding still protects this page lifetime.
	}
	return created;
}

function readStoredIntent(): PendingAccessIntent | null {
	try {
		const serialized = getSessionStorage()?.getItem(PENDING_INTENT_KEY);
		if (!serialized || serialized.length > 2048) return null;
		return JSON.parse(serialized) as PendingAccessIntent;
	} catch {
		return null;
	}
}

function isValidPendingIntent(value: unknown): value is PendingAccessIntent {
	if (!value || typeof value !== 'object') return false;
	const intent = value as Partial<PendingAccessIntent>;
	if (!isReplayableAction(intent.action ?? null)) return false;
	if (typeof intent.rawAction !== 'string' || intent.rawAction.length === 0 || intent.rawAction.length > 512) return false;
	if (typeof intent.recordedAt !== 'number' || !Number.isFinite(intent.recordedAt)) return false;
	if (typeof intent.browserSessionId !== 'string' || intent.browserSessionId.length === 0 || intent.browserSessionId.length > 160) return false;
	if (intent.recordedUserId != null && !isSafeUserId(intent.recordedUserId)) return false;
	return true;
}
